/**
 * Period chips — one per materialised snapshot.
 *
 * The last chip is the LIVE frame: the establishment as at the most recent F&O
 * sync, not a real-time read. It is labelled "Today" with the sync time on the
 * badge and "as at last sync" underneath, so nobody can mistake an overnight
 * frame for a live one. If the API sends no source_sync_at the badge says so
 * rather than quietly implying freshness.
 */
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  Def,
  fmtDay,
  fmtSyncTimeOnly,
  type DefLookup,
  type WorkforcePeriod,
} from './apiContract';

interface PeriodChipsProps {
  periods: WorkforcePeriod[];
  value: string | null;
  onChange: (asAt: string) => void;
  def: DefLookup;
  disabled?: boolean;
}

export const PeriodChips: React.FC<PeriodChipsProps> = ({
  periods,
  value,
  onChange,
  def,
  disabled,
}) => (
  <div className="flex items-center gap-2">
    <span className="flex items-center text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-500">
      As at
      <Def
        def={def}
        k="__period__"
        text="Each chip is an independently reconstructed snapshot of the establishment at that date. The last chip is the most recent F&O sync — labelled Today, but it is the overnight frame, not a live read."
      />
    </span>
    <div className="flex flex-wrap gap-1.5">
      {periods.map((p) => {
        const selected = p.as_at === value;
        const live = !!p.is_live;
        const syncTime = fmtSyncTimeOnly(p.source_sync_at);
        const sub = live
          ? syncTime
            ? `as at last sync · ${fmtDay(p.as_at)}`
            : 'as at last sync'
          : p.sub;
        return (
          <button
            key={p.as_at}
            type="button"
            aria-pressed={selected}
            disabled={disabled}
            onClick={() => onChange(p.as_at)}
            title={live ? `Establishment as at the last F&O sync (${fmtDay(p.as_at)})` : fmtDay(p.as_at)}
            className={cn(
              'rounded-full border px-3 py-1.5 text-left text-[12px] font-semibold leading-tight tabular-nums transition-colors',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-future-blue',
              selected
                ? 'border-future-blue bg-future-blue text-white'
                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50',
              disabled && 'cursor-not-allowed opacity-60',
            )}
          >
            <span className="flex items-center gap-1.5">
              {live ? 'Today' : p.label}
              {live ? (
                <Badge
                  variant="outline"
                  className={cn(
                    'rounded px-1 py-0 text-[9px] font-medium leading-[14px]',
                    selected ? 'border-white/50 text-white' : 'border-gray-300 text-gray-500',
                  )}
                >
                  {syncTime ? `sync ${syncTime}` : 'sync time unknown'}
                </Badge>
              ) : null}
            </span>
            {sub ? (
              <span className={cn('mt-0.5 block text-[9.5px] font-normal', selected ? 'text-white/80' : 'text-gray-400')}>
                {sub}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  </div>
);
